import { type FormEvent, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router'

import { useLocales } from '../../extensions/hooks'
import {
  Button,
  ErrorAlert,
  LanguageSelect,
  TextInput,
} from '../../shared/components'
import { resolveTheme, useUiStore } from '../../shared/hooks'
import { interpolate, useT } from '../../shared/i18n'
import { bandTheme, formTheme } from '../../shared/theme'

import { login } from './auth.api'
import { useAuthStore } from './hooks'

export function LoginPage() {
  const status = useAuthStore((s) => s.status)
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)
  const { t } = useT()
  const { locales } = useLocales()
  const { theme, design } = useUiStore()
  // The band follows the theme's chrome; the form its appearance (ADR-056, ADR-068).
  const band = bandTheme(design, resolveTheme(theme))
  const from = (location.state as { from?: string } | null)?.from ?? '/'

  if (status === 'authenticated') return <Navigate to={from} replace />

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)

    try {
      await login(email, password)
      navigate(from, { replace: true })
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="login">
      <section className="login__band" data-theme={band}>
        <img
          src={`/brand/gxlabs-logo-stacked-${band === 'dark' ? 'white' : 'black'}.png`}
          alt="GXLabs"
        />
        <div>
          <h1 className="login__hero">
            {interpolate(t.auth.hero, { em: <em>{t.auth.heroEm}</em> })}
          </h1>
          <p className="login__sub">{t.auth.sub}</p>
        </div>
        <p className="login__caption">© GXLabs Kft.</p>
      </section>
      <section
        className="login__form"
        data-theme={formTheme(design, resolveTheme(theme))}
      >
        <form
          onSubmit={submit}
          className="stack"
          style={{ maxWidth: 400, width: '100%', margin: '0 auto' }}
          noValidate
        >
          <header>
            <p className="page-header__kicker">{t.auth.welcome}</p>
            <h2 className="page-header__title">{t.auth.signIn}</h2>
          </header>
          {!!error && <ErrorAlert error={error} title={t.auth.failed} />}
          <TextInput
            label={t.common.email}
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />
          <TextInput
            label={t.common.password}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <div>
            <Button
              type="submit"
              variant="primary"
              loading={busy}
              disabled={!email || !password}
            >
              {t.auth.signIn}
            </Button>
          </div>
          {locales.length > 1 && <LanguageSelect locales={locales} />}
        </form>
      </section>
    </div>
  )
}
